/**
 * Business categories taxonomy used by the business onboarding flow.
 *
 * Values are stored as plain strings in `categoryGroup` and `categoryDetail`.
 * Keep labels reasonably short (server validation limits apply).
 */

export const BUSINESS_CATEGORIES = [
  {
    group: 'Food & Beverage',
    subcategories: [
      'Restaurant (Full Service)',
      'Restaurant (Quick Service / Fast Food)',
      'Café / Coffee Shop',
      'Bakery / Pastry Shop',
      'Bar / Pub',
      'Nightclub / Lounge',
      'Food Truck / Street Food',
      'Catering',
      'Dessert Shop',
      'Ice Cream / Frozen Yogurt',
      'Juice / Smoothie Bar',
      'Brewery',
      'Winery',
      'Distillery',
      'Specialty Food Store',
      'Other / Not listed',
    ],
  },
  {
    group: 'Accommodation & Lodging',
    subcategories: [
      'Hotel',
      'Boutique Hotel',
      'Hostel',
      'Bed & Breakfast',
      'Resort',
      'Vacation Rental / Property Manager',
      'Serviced Apartments',
      'Campground / RV Park',
      'Glamping',
      'Homestay',
      'Other / Not listed',
    ],
  },
  {
    group: 'Tours & Experiences',
    subcategories: [
      'City Tour',
      'Historical / Cultural Tour',
      'Food Tour',
      'Wine / Beer Tour',
      'Adventure Tour',
      'Hiking / Trekking Tour',
      'Cycling Tour',
      'Boat / Water Tour',
      'Diving / Snorkeling Experience',
      'Surf Lesson / Surf School',
      'Ski / Snowboard School',
      'Workshop / Class',
      'Attraction / Landmark',
      'Theme Park / Water Park',
      'Zoo / Aquarium',
      'Escape Room',
      'Other / Not listed',
    ],
  },
  {
    group: 'Transportation & Mobility',
    subcategories: [
      'Taxi / Chauffeur / Private Driver',
      'Car Rental',
      'Scooter / Motorbike Rental',
      'Bicycle Rental',
      'Shuttle / Airport Transfer',
      'Bus / Coach Operator',
      'Boat / Ferry Operator',
      'Helicopter / Private Air',
      'Parking',
      'Other / Not listed',
    ],
  },
  {
    group: 'Travel & Tourism Services',
    subcategories: [
      'Travel Agency',
      'Tour Operator / DMC',
      'Concierge / Trip Planning Service',
      'Tourist Information Center',
      'Travel Insurance',
      'Visa / Immigration Assistance',
      'Other / Not listed',
    ],
  },
  {
    group: 'Retail & Shopping',
    subcategories: [
      'Convenience Store',
      'Grocery / Market',
      'Apparel / Fashion',
      'Shoes',
      'Jewelry',
      'Gifts / Souvenirs',
      'Electronics',
      'Sports & Outdoors Retail',
      'Beauty / Cosmetics',
      'Pharmacy / Health Store',
      'Liquor Store',
      'Bookstore / Stationery',
      'Home & Decor',
      'Other / Not listed',
    ],
  },
  {
    group: 'Wellness, Beauty & Personal Care',
    subcategories: [
      'Hair Salon',
      'Barber',
      'Nail Salon',
      'Spa',
      'Massage Therapy',
      'Beauty Clinic',
      'Fitness Studio / Gym',
      'Yoga / Pilates',
      'Personal Training',
      'Wellness Center',
      'Other / Not listed',
    ],
  },
  {
    group: 'Health & Medical',
    subcategories: [
      'Clinic / Doctor',
      'Dentist',
      'Optometrist',
      'Physiotherapy',
      'Chiropractic',
      'Mental Health Services',
      'Laboratory / Diagnostics',
      'Veterinary',
      'Other / Not listed',
    ],
  },
  {
    group: 'Professional Services',
    subcategories: [
      'Legal Services',
      'Accounting / Tax',
      'Consulting',
      'Marketing / Advertising',
      'Photography / Videography',
      'IT Services / Support',
      'Web / Software Development',
      'Translation / Interpretation',
      'Printing / Design',
      'Event Planning',
      'Cleaning Services',
      'Security Services',
      'Other / Not listed',
    ],
  },
  {
    group: 'Home & Construction Services',
    subcategories: [
      'General Contractor',
      'Plumbing',
      'Electrical',
      'HVAC',
      'Landscaping / Gardening',
      'Pest Control',
      'Painting',
      'Handyman / Home Repair',
      'Moving / Storage',
      'Locksmith',
      'Interior Design',
      'Other / Not listed',
    ],
  },
  {
    group: 'Education & Training',
    subcategories: [
      'Language School',
      'Tutoring',
      'Vocational Training',
      'Cooking School',
      'Dance School',
      'Music Lessons',
      'Art Classes',
      'Workshops / Seminars',
      'Other / Not listed',
    ],
  },
  {
    group: 'Arts, Culture & Entertainment',
    subcategories: [
      'Museum',
      'Gallery',
      'Theater',
      'Live Music Venue',
      'Cinema',
      'Cultural Center',
      'Comedy Club',
      'Arcade',
      'Other / Not listed',
    ],
  },
  {
    group: 'Sports & Outdoors',
    subcategories: [
      'Sports Club',
      'Climbing Gym',
      'Golf Course',
      'Tennis Club',
      'Adventure Park',
      'Equipment Rental',
      'Other / Not listed',
    ],
  },
  {
    group: 'Events & Venues',
    subcategories: [
      'Event Venue',
      'Conference Center',
      'Wedding Venue',
      'Exhibition Center',
      'Stadium / Arena',
      'Community Hall',
      'Other / Not listed',
    ],
  },
  {
    group: 'Real Estate & Property',
    subcategories: [
      'Real Estate Agency',
      'Property Management',
      'Co-working Space',
      'Storage Facility',
      'Other / Not listed',
    ],
  },
  {
    group: 'Automotive',
    subcategories: [
      'Auto Repair',
      'Car Wash / Detailing',
      'Tire Shop',
      'Fuel Station / EV Charging',
      'Dealership',
      'Other / Not listed',
    ],
  },
  {
    group: 'Pets & Animals',
    subcategories: [
      'Pet Grooming',
      'Pet Boarding / Daycare',
      'Pet Training',
      'Pet Sitting / Dog Walking',
      'Pet Supplies',
      'Other / Not listed',
    ],
  },
  {
    group: 'Finance & Insurance',
    subcategories: [
      'Insurance Agency',
      'Financial Advisory / Consulting',
      'Money Transfer / Currency Exchange',
      'Other / Not listed',
    ],
  },
  {
    group: 'Manufacturing, Wholesale & Distribution',
    subcategories: [
      'Manufacturer',
      'Wholesale Distributor',
      'Import / Export',
      'Warehouse / Logistics',
      'Other / Not listed',
    ],
  },
  {
    group: 'Agriculture & Food Production',
    subcategories: [
      'Farm / Producer',
      'Food Producer / Packaged Goods',
      'Other / Not listed',
    ],
  },
  {
    group: 'Technology & Digital',
    subcategories: [
      'SaaS / Software',
      'App / Platform',
      'Telecom / ISP',
      'Electronics Repair',
      'Gaming / Esports',
      'Other / Not listed',
    ],
  },
  {
    group: 'Nonprofit & Community',
    subcategories: [
      'Nonprofit / Charity',
      'Community Organization',
      'Religious Organization',
      'Government / Public Service',
      'Other / Not listed',
    ],
  },
  {
    group: 'Other',
    subcategories: ['Other / Not listed'],
  },
];

export const getSubcategoriesForGroup = (group) => {
  const match = BUSINESS_CATEGORIES.find((c) => c.group === group);
  return match?.subcategories || [];
};
